/**
 * The page this tab entered the site on, and where from (`shared/entry.ts`).
 *
 * Held in memory and nowhere else, so it needs no consent and is counted for
 * everybody. A sign-in that leaves the page — Google's redirect, an e-mail
 * link opened later or on another device — would lose it, so the address it
 * comes back to carries it instead (`withEntry`), and the load that comes
 * back reads it from there and takes it out of the address bar.
 */
import { ENTRY_PARAMS, entryPath, PASS_THROUGH, sourceName, withoutLocale, type Entry } from "@shared/entry";
import { PRERENDER_ROUTES } from "@/routes";

/** The pages an entry may name: the public ones, and the app a link can open on. */
const PAGES: ReadonlySet<string> = new Set([...PRERENDER_ROUTES, "/app", "/pass"]);

function read(): { entry: Entry | null; isNew: boolean; carried: boolean } {
  const url = new URL(window.location.href);
  const carried = url.searchParams.get(ENTRY_PARAMS.path);
  if (carried !== null) {
    const entry = { path: carried, source: url.searchParams.get(ENTRY_PARAMS.source) ?? "" };
    url.searchParams.delete(ENTRY_PARAMS.path);
    url.searchParams.delete(ENTRY_PARAMS.source);
    // Firebase reads its own parameters off the address later; ours only go.
    window.history.replaceState(window.history.state, "", url.pathname + url.search + url.hash);
    return { entry: carried.startsWith("/") ? entry : null, isNew: false, carried: true };
  }
  // An e-mail link from before the address carried the entry.
  if (url.searchParams.has("oobCode")) return { entry: null, isNew: false, carried: false };

  let host = "";
  try {
    host = document.referrer ? new URL(document.referrer).hostname : "";
  } catch {
    /* an unparseable referrer is no referrer */
  }
  // A page of ours opened from another: the entry was earlier.
  if (host === url.hostname) return { entry: null, isNew: false, carried: false };
  // A reload, or back to a page this tab had already left, keeps the first
  // load's referrer and is the same visit: its entry, but not counted again.
  const how = (performance.getEntriesByType("navigation")[0] as PerformanceNavigationTiming | undefined)?.type;
  const again = how === "reload" || how === "back_forward";
  // Back from signing in or paying: a step, not a visit.
  if (host && PASS_THROUGH.test(host)) return { entry: null, isNew: false, carried: false };
  // Ours, not a visitor's.
  if (withoutLocale(url.pathname) === "/admin") return { entry: null, isNew: false, carried: false };
  return {
    entry: { path: entryPath(url.pathname, PAGES), source: sourceName(url.searchParams.get("utm_source"), host) },
    isNew: !again,
    carried: false,
  };
}

const visit =
  typeof window === "undefined" || navigator.webdriver ? { entry: null, isNew: false, carried: false } : read();

if (visit.isNew && visit.entry) {
  try {
    void fetch("/api/entry", {
      method: "POST",
      keepalive: true,
      headers: { "content-type": "application/json" },
      body: JSON.stringify(visit.entry),
    }).catch(() => undefined);
  } catch {
    /* not counted: nothing the visitor would notice */
  }
}

/**
 * Whether the `/session` about to be sent is the first of a visit that was
 * counted: a fresh entry, or one that went away to sign in and came back
 * carrying its entry. True once per page load, so an account that already
 * exists can be counted as a returning visitor rather than as one more reader
 * who did not sign up.
 */
let returnReported = false;
export function takeFreshEntry(): boolean {
  if (!visit.entry || returnReported) return false;
  returnReported = true;
  return visit.isNew || visit.carried;
}

/** What to send with the `/session` that may create an account. */
export const currentEntry = (): Entry | null => visit.entry;

/** An absolute `url` with this tab's entry in its query, for an address the sign-in comes back to. */
export function withEntry(url: string): string {
  if (!visit.entry) return url;
  const out = new URL(url);
  out.searchParams.set(ENTRY_PARAMS.path, visit.entry.path);
  out.searchParams.set(ENTRY_PARAMS.source, visit.entry.source);
  return out.toString();
}
